import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import {
  getRequest,
  patchRequest,
  postRequest,
} from "../../../config/axois/apiRequest";
import { showError, showSuccess } from "../../../components/alerts/alerts";
import { OK } from "../../../config/axois/apiStatusCode";
import { consultantApi } from "../../../config/apiUrls/apiUrl";
import { errorHandler, isEmpty } from "../../../helper/helper";

export interface Consultant {
  id: string | number;
  name: string;
  type: string;
  phone_no: string;
  email?: string | null;
  note?: string | null;
}

interface ConsultantListing extends Consultant {
  created_by: string;
  deleted_at?: string;
}

interface ConsultantState {
  consultants: ConsultantListing[];
  consultantsList: ConsultantListing[];
  details: Consultant | null;
  loading: boolean;
  consultantsListLoading: boolean;
  detailsLoading: boolean;
  error: string | null;
}

interface QueryParam {
  currentOffset: number;
  limit: number;
  search?: string;
  projectId?: number;
  projectIdList?: number;
  ordering?: string;
  isModal?: boolean;
  isDeleted?: boolean;
}

const initialState: ConsultantState = {
  consultants: [],
  consultantsList: [],
  details: null,
  loading: false,
  consultantsListLoading: false,
  detailsLoading: false,
  error: null,
};

export const fetchConsultants: any = createAsyncThunk(
  "consultants/fetchConsultants",
  async ({
    currentOffset,
    limit,
    search,
    projectId,
    ordering,
    projectIdList,
    isModal,
    isDeleted,
  }: QueryParam) => {
    try {
      let url = `${consultantApi.getConsultants}?limit=${limit}&offset=${currentOffset}`;

      if (!isEmpty(search)) {
        url += `&search=${search}`;
      }

      if (!isEmpty(projectId)) {
        url += `&consultant_without_project_id=${projectId}`;
      }

      if (!isEmpty(projectIdList)) {
        url += `&project_id=${projectIdList}`;
      }

      if (!isEmpty(ordering)) {
        url += `&ordering=${ordering}`;
      }

      if (isDeleted) {
        url += `&with_trashed=true`;
      }

      const response = await getRequest(url, true);

      return { ...response, isModal: isModal };
    } catch (error: any) {
      return error;
    }
  }
);

export const clearConsultant: any = createAsyncThunk(
  "consultants/clearConsultant",
  async (value: boolean) => {
    return value;
  }
);

export const createConsultant = createAsyncThunk(
  "consultants/createConsultant",
  async (newVendor: Omit<Consultant, "id">) => {
    // Omitting "id" from newVendor
    try {
      const response = await postRequest(
        consultantApi.createConsultant,
        newVendor,
        true
      );

      return response;
    } catch (error: any) {
      if (error?.response?.data?.errors) {
        errorHandler(error?.response?.data?.errors);
        return;
      } else {
        showError("Something Went Wrong");
      }

      return error;
    }
  }
);

export const updateConsultant = createAsyncThunk(
  "consultants/updateConsultant",
  async (updatedConsultant: Consultant) => {
    try {
      const response = await patchRequest(
        consultantApi.updateConsultant + updatedConsultant.id,
        updatedConsultant,
        true
      );

      return response;
    } catch (error: any) {
      if (error?.response?.data?.errors) {
        errorHandler(error?.response?.data?.errors);
        return;
      } else {
        showError("Something Went Wrong");
      }

      return error;
    }
  }
);

export const getConsultantById = createAsyncThunk(
  "consultants/getConsultantById",
  async (consultantId: any) => {
    try {
      const response = await getRequest(
        `${consultantApi.getConsultants}/${consultantId}`,
        true
      );

      return response;
    } catch (error: any) {
      return error;
    }
  }
);

const consultantsSlice = createSlice({
  name: "consultants",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchConsultants.pending, (state, action) => {
        if (action?.meta?.arg?.isModal) {
          state.consultantsListLoading = true;
        } else {
          state.loading = true;
        }

        state.error = null;
      })
      .addCase(fetchConsultants.fulfilled, (state, action) => {
        // only for add vendor modal in project details
        if (action.payload?.isModal) {
          state.consultantsListLoading = false;
          state.consultantsList = [
            ...state.consultantsList,
            ...(action.payload?.data?.data ?? []),
          ];
        } else {
          state.loading = false;
          state.consultants = action.payload?.data?.data ?? [];
        }
      })
      .addCase(clearConsultant.fulfilled, (state, action) => {
        state.consultantsList = [];
      })
      .addCase(fetchConsultants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch consultants";
      })
      .addCase(createConsultant.fulfilled, (state, action) => {
        if ([200, 201].includes(action?.payload?.status)) {
          showSuccess("Consultant Added Successfully");
        }
      })
      .addCase(updateConsultant.fulfilled, (state, action) => {
        if ([200, 201].includes(action?.payload?.status)) {
          showSuccess("Consultant Updated Successfully");
        }
      })
      .addCase(getConsultantById.pending, (state) => {
        state.detailsLoading = true;
        state.error = null;
      })
      .addCase(getConsultantById.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.details = action?.payload?.data?.data ?? null;
      })
      .addCase(getConsultantById.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.error.message || "Failed to fetch vendor";
      });
  },
});

// Selectors
export const consultantsSelectors = (state: RootState) => state.consultants;

const consultantsReducer = consultantsSlice.reducer;

export default consultantsReducer;
